import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaCog, FaInfoCircle } from 'react-icons/fa';
import { getUrlParams } from '../apiService/timespam';
import { API_BASE_URL } from '../apiService/apiClient';
import './CycleTracker.css';

const BASE_URL = `${API_BASE_URL}/goqiiapihealthactivity`;

const CycleTracker = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userid } = useParams();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const queryUserId = queryParams.get('goqiiUserId');

  const { lang, encryptedId } = getUrlParams();
  const USER_ID = location.state?.goqiiUserId || encryptedId || userid || queryUserId;

  useEffect(() => {
    if (!USER_ID) {
      console.error("USER_ID missing - check the URL/state goqiiUserId param.");
    }
  }, [USER_ID]);

  const today = new Date();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [activeTab, setActiveTab] = useState('cycle');
  const [selectedDate, setSelectedDate] = useState(today.getDate());
  const [showInsight, setShowInsight] = useState(true);
  const [showLogModal, setShowLogModal] = useState(false);
  const [showEndPeriodModal, setShowEndPeriodModal] = useState(false);
  const [showFaqModal, setShowFaqModal] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState(-1);
  const [showPregnancyConfirmModal, setShowPregnancyConfirmModal] = useState(false);
  const [pendingPregnancyFlag, setPendingPregnancyFlag] = useState(1);
  const [pregnancyWeekData, setPregnancyWeekData] = useState(null);
  const [pregnancyViewDate, setPregnancyViewDate] = useState(
    `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
  );

  const [markedDates, setMarkedDates] = useState({
    menstruation: [],
    fertile: [],
    ovulation: [],
    predicted: []
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [apiData, setApiData] = useState(null);
  const [summaryData, setSummaryData] = useState(null);

  const latestPredictionDateRef = useRef(null);

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    fetchPredictionAPI();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, currentDate]);

  const isPregnancyMode = Number(apiData?.trackPregnancy) === 1;

  useEffect(() => {
    if (isPregnancyMode) {
      setActiveTab('pregnancy');
    }
  }, [isPregnancyMode]);

  useEffect(() => {
    if (activeTab === 'pregnancy' && isPregnancyMode && USER_ID) {
      fetchWeeksAPI(pregnancyViewDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isPregnancyMode, USER_ID]);

  // --- API FUNCTIONS ---

  const fetchPredictionAPI = async () => {
    const selectedFullDate = formatDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth(), selectedDate)
    );
    // Guards against out-of-order responses: if the user clicks another date
    // before this request resolves, its result must not overwrite the newer selection.
    latestPredictionDateRef.current = selectedFullDate;

    try {
      setLoading(true);
      const response = await fetch(`${BASE_URL}/fetch_predictions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "lang": lang
        },
        body: JSON.stringify({
          goqiiUserId: USER_ID,
          predictionDate: selectedFullDate
        })
      });
      const result = await response.json();

      if (latestPredictionDateRef.current !== selectedFullDate) return;

      if (result?.code === 200 && result?.data) {
        const data = result.data;

        setApiData(data);

        setMarkedDates({
          menstruation: data.cyclePhases?.menstruation || [],
          fertile: data.cyclePhases?.fertile || [],
          ovulation: data.cyclePhases?.ovulation || [],
          predicted: data.cyclePhases?.predicted || []
        });

        setSummaryData(data.currentDayInfo);
      }
    } catch (err) {
      if (latestPredictionDateRef.current !== selectedFullDate) return;
      console.error(err);
      setError("Failed to fetch cycle data.");
    } finally {
      if (latestPredictionDateRef.current === selectedFullDate) setLoading(false);
    }
  };

  const fetchWeeksAPI = async (date) => {
    try {
      const response = await fetch(`${BASE_URL}/fetch_weeks`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "lang": lang },
        body: JSON.stringify({
          goqiiUserId: USER_ID,
          date: date || pregnancyViewDate
        })
      });
      const result = await response.json();
      if (result?.code === 200 && result?.data) {
        setPregnancyWeekData(result.data);
      }
    } catch (err) {
      console.error(err);
      setError("Failed to fetch pregnancy details.");
    }
  };

  const sendPredictionData = async (date) => {
    try {
      setLoading(true);

      const start = new Date(currentDate.getFullYear(), currentDate.getMonth(), date);
      const formattedStartDate = formatDate(start);

      const periodLength = Number(summaryData?.menstrual?.totalDays) || 5;
      const end = new Date(start);
      end.setDate(start.getDate() + periodLength - 1);
      const formattedEndDate = formatDate(end);

      setMarkedDates((prev) => {
        const updatedMenstruation = prev.menstruation.includes(formattedStartDate)
          ? prev.menstruation
          : [...prev.menstruation, formattedStartDate];

        const updatedPredicted = prev.predicted.filter(d => d !== formattedStartDate);

        return {
          ...prev,
          menstruation: updatedMenstruation,
          predicted: updatedPredicted
        };
      });

      setSummaryData((prev) => ({
        ...prev,
        menstrual: { currentDay: 1, totalDays: prev?.menstrual?.totalDays || 5 },
        periodLateBy: 0
      }));

      await fetch(`${BASE_URL}/save_women_log_by_dates`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "lang": lang },
        body: JSON.stringify({
          goqiiUserId: USER_ID,
          startDate: formattedStartDate,
          endDate: formattedEndDate
        })
      });

      setShowLogModal(false);

      await fetchPredictionAPI();

    } catch (err) {
      console.error(err);
      setError("Failed to log period.");
    } finally {
      setLoading(false);
    }
  };

  const endOrDeletePeriod = async (endDateStr) => {
    try {
      setLoading(true);
      await fetch(`${BASE_URL}/update_period`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "lang": lang },
        body: JSON.stringify({
          goqiiUserId: USER_ID,
          autoId: apiData?.autoId,
          endDate: endDateStr
        })
      });
      setShowEndPeriodModal(false);
      await fetchPredictionAPI();
    } catch (err) {
      console.error(err);
      setError("Failed to update period.");
    } finally {
      setLoading(false);
    }
  };

  const activatePregnancy = async (flag) => {
    try {
      setLoading(true);
      await fetch(`${BASE_URL}/activate_women_resource`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "lang": lang },
        body: JSON.stringify({
          goqiiUserId: USER_ID,
          pregnantflag: flag
        })
      });
      setShowPregnancyConfirmModal(false);
      await fetchPredictionAPI();
      if (flag === 1) {
        await fetchWeeksAPI();
      }
    } catch (err) {
      console.error(err);
      setError("Failed to update pregnancy mode.");
    } finally {
      setLoading(false);
    }
  };

  // --- UI HELPERS ---

  const getImageByName = (type, name) => {
    if (!apiData) return null;

    const list =
      type === 'flow' ? apiData.flowResult
        : type === 'mood' ? apiData.moodResult
          : apiData.symptomResult;

    return list?.find(item => item.name?.toLowerCase() === name?.toLowerCase());
  };

  const monthNames = t("cycleTracker.months", { returnObjects: true });
  const dayNames = t("cycleTracker.days", { returnObjects: true });
  const faqItems = t("cycleTracker.faq.items", { returnObjects: true }) || [];

  const getDaysInMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const getFirstDayOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1).getDay();

  const goToMonth = (monthOffset) => {
    const target = new Date(currentDate.getFullYear(), currentDate.getMonth() + monthOffset, 1);
    const daysInTarget = getDaysInMonth(target);
    setSelectedDate((day) => Math.min(day, daysInTarget));
    setCurrentDate(target);
  };
  const previousMonth = () => goToMonth(-1);
  const nextMonth = () => goToMonth(1);
  const handleDateClick = (day) => setSelectedDate(day);

  const openLogModal = () => setShowLogModal(true);
  const closeLogModal = () => setShowLogModal(false);
  const logPeriod = () => sendPredictionData(selectedDate);

  const openEndPeriodModal = () => setShowEndPeriodModal(true);
  const closeEndPeriodModal = () => setShowEndPeriodModal(false);
  const confirmEndPeriod = () => endOrDeletePeriod(selectedFullDateValue());

  const openFaqModal = () => { setOpenFaqIndex(-1); setShowFaqModal(true); };
  const closeFaqModal = () => setShowFaqModal(false);

  const openPregnancyConfirm = (flag) => { setPendingPregnancyFlag(flag); setShowPregnancyConfirmModal(true); };
  const closePregnancyConfirm = () => setShowPregnancyConfirmModal(false);

  const goToPregnancyDate = (dateStr) => {
    setPregnancyViewDate(dateStr);
    fetchWeeksAPI(dateStr);
  };

  const shiftPregnancyDate = (days) => {
    const d = new Date(pregnancyViewDate);
    d.setDate(d.getDate() + days);
    goToPregnancyDate(formatDate(d));
  };

  const formatPregnancyViewDate = () => {
    const d = new Date(pregnancyViewDate);
    return `${String(d.getDate()).padStart(2, '0')} ${monthNames[d.getMonth()].charAt(0) + monthNames[d.getMonth()].slice(1).toLowerCase()} ${d.getFullYear()}`;
  };

  const openLogFlowMoodSymptoms = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const dateStr = formatDate(new Date(year, month, selectedDate));

    navigate(`/log${location.search}`, {
      state: {
        date: dateStr,
        selectedDate,
        currentDate,
        goqiiUserId: USER_ID
      }
    });
  };

  const formatSelectedDate = () => {
    const year = currentDate.getFullYear();
    const monthIndex = currentDate.getMonth();
    const day = String(selectedDate).padStart(2, '0');
    const month = monthNames[monthIndex];
    return `${day} ${month.charAt(0) + month.slice(1).toLowerCase()} ${year}`;
  };

  const getDayClass = (day) => {
    const classes = ['calendar-day'];
    const fullDate = formatDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), day));

    // Mutually exclusive, in the same priority order as the legacy calendar:
    // period > fertile > predicted. Ovulation is a badge overlay, not a
    // background fill, so it can safely stack on top of any of the above.
    if (markedDates.menstruation.includes(fullDate)) classes.push('menstruation');
    else if (markedDates.fertile.includes(fullDate)) classes.push('fertile');
    else if (markedDates.predicted.includes(fullDate)) classes.push('predicted');

    if (markedDates.ovulation.includes(fullDate)) classes.push('ovulation');

    if (day === selectedDate) classes.push('selected');

    return classes.join(' ');
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];

    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push(
        <div
          key={day}
          className={getDayClass(day)}
          onClick={() => handleDateClick(day)}
          data-testid={`calendar-day-${day}`}
        >
          {day}
        </div>
      );
    }
    return days;
  };

  const toggleInsight = () => setShowInsight(!showInsight);

  const selectedFullDateValue = () => formatDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), selectedDate));
  const selectedFullDate = selectedFullDateValue();
  const currentLogData = apiData?.loggedData;
  const periodStartDate = markedDates.menstruation.length > 0 ? markedDates.menstruation[0] : null;
  const isSelectedInPeriod = markedDates.menstruation.includes(selectedFullDate);
  const isDeletingPeriod = selectedFullDate === periodStartDate;

  // Inclusive day count between two 'YYYY-MM-DD' strings, matching the legacy
  // DB_Cards::getDatesFromRange() day count used throughout Women.php.
  const daysBetweenInclusive = (start, end) => Math.round((new Date(end) - new Date(start)) / 86400000) + 1;

  // Ports the legacy DB_Women::fetch_predictions() row-building logic (goqiiv2
  // php/women, ~Women.php:360-652) to the frontend: which rows appear and what
  // they say depends on which phase the selected date falls into, rather than
  // always showing a fixed Period/Cycle/Next/Last set.
  const buildInsightRows = () => {
    const cycleArr = markedDates.menstruation;
    const fertileArr = markedDates.fertile;
    const predictedArr = markedDates.predicted;
    if (!cycleArr.length) return [];

    const cycleStart = cycleArr[0];
    const lastMenstruationDay = cycleArr[cycleArr.length - 1];
    const firstPredicted = predictedArr[0];
    const cycleLength = summaryData?.cycle?.totalDays;
    const periodLength = summaryData?.menstrual?.totalDays;
    const isLongCycle = Number(cycleLength) >= 21;

    const menstruationIdx = cycleArr.indexOf(selectedFullDate);
    const inMenstruation = menstruationIdx !== -1;
    const fertileIdx = fertileArr.indexOf(selectedFullDate);
    const inFertile = fertileIdx !== -1;
    const predictedIdx = predictedArr.indexOf(selectedFullDate);
    const inPredicted = predictedIdx !== -1;
    const inFollicularGap = !inMenstruation && !inFertile && !inPredicted &&
      lastMenstruationDay && fertileArr[0] &&
      selectedFullDate > lastMenstruationDay && selectedFullDate < fertileArr[0];

    const days = t('cycleTracker.insights.days');
    const day = t('cycleTracker.insights.day');
    const rows = [];

    if (inMenstruation) {
      rows.push({
        label: t('cycleTracker.insights.menstrualPeriod', 'Menstrual period'),
        value: `${day} ${menstruationIdx + 1}/${periodLength ?? '-'}`
      });
    }

    if (inPredicted) {
      rows.push({
        label: t('cycleTracker.insights.predictedPeriod', 'Predicted Period'),
        value: `${day} ${predictedIdx + 1}/${periodLength ?? '-'}`
      });
    } else if (inFertile && isLongCycle) {
      rows.push({
        label: t('cycleTracker.insights.fertileEndsIn', 'Fertile Window ends in'),
        value: `${fertileArr.length - 1 - fertileIdx} ${days}`
      });
    } else if (firstPredicted && selectedFullDate >= cycleStart && selectedFullDate < firstPredicted) {
      rows.push({
        label: t('cycleTracker.insights.currentCycle', 'Current Cycle'),
        value: `${day} ${daysBetweenInclusive(cycleStart, selectedFullDate)}/${cycleLength ?? '-'}`
      });
    }

    if (isLongCycle && fertileArr[0] && (inMenstruation || inFollicularGap)) {
      rows.push({
        label: t('cycleTracker.insights.fertileStartsIn', 'Fertile Window starts in'),
        value: `${daysBetweenInclusive(selectedFullDate, fertileArr[0]) - 1} ${days}`
      });
    } else if (firstPredicted && selectedFullDate < firstPredicted) {
      rows.push({
        label: t('cycleTracker.insights.periodStartsIn', 'Menstrual Period starts in'),
        value: `${daysBetweenInclusive(selectedFullDate, firstPredicted) - 1} ${days}`
      });
    }

    if (firstPredicted) {
      const realToday = formatDate(new Date());
      if (selectedFullDate > firstPredicted && selectedFullDate <= realToday) {
        rows.push({
          label: t('cycleTracker.insights.periodLateBy', 'Menstrual period late by'),
          value: `${daysBetweenInclusive(firstPredicted, selectedFullDate)} ${days}`
        });
      } else if (selectedFullDate < firstPredicted) {
        rows.push({
          label: t('cycleTracker.insights.periodAheadFor', 'Menstrual period ahead for'),
          value: `${daysBetweenInclusive(selectedFullDate, firstPredicted) - 1} ${days}`
        });
      }
    }

    return rows;
  };

  const insightRows = buildInsightRows();

  return (
    <div className="cycle-tracker" data-testid="cycle-tracker">
      {loading && (
        <div className="loading-overlay" data-testid="loading-overlay">
          <div className="spinner"></div>
        </div>
      )}

      {error && (
        <div className="error-banner" data-testid="error-banner">
          <span>{error}</span>
          <button className="error-close" onClick={() => setError(null)}>&times;</button>
        </div>
      )}

      <header className="header">
        <h1 className="title">{t("cycleTracker.header")}</h1>
        <div className="header-icons">
          <a
            className="icon-btn"
            href="https://goqii.com/app/?fsn=216&fssn=0"
            data-testid="settings-btn"
            aria-label={t('cycleTracker.buttons.settings')}
          >
            <FaCog size={22} />
          </a>
          <button className="icon-btn" onClick={openFaqModal} data-testid="faq-btn" aria-label={t('cycleTracker.buttons.faq')}>
            <FaInfoCircle size={22} />
          </button>
        </div>
      </header>

      <div className="tabs" style={isPregnancyMode ? { gridTemplateColumns: '1fr' } : undefined}>
        {!isPregnancyMode && (
          <button
            className={`tab ${activeTab === 'cycle' ? 'active' : ''}`}
            onClick={() => setActiveTab('cycle')}
            data-testid="track-cycle-tab"
          >
            {t("cycleTracker.tabs.trackCycle")}
          </button>
        )}
        <button
          className={`tab ${activeTab === 'pregnancy' ? 'active' : ''}`}
          onClick={() => setActiveTab('pregnancy')}
          data-testid="track-pregnancy-tab"
        >
          {t("cycleTracker.tabs.trackPregnancy")}
        </button>
      </div>

      {activeTab === 'cycle' && (
        <>
          <div className="calendar-container" data-testid="calendar-container">
            <div className="calendar-header">
              <button className="nav-btn" onClick={previousMonth} data-testid="prev-month-btn" aria-label="Previous month">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
              </button>
              <h2 className="month-year" data-testid="month-year">
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h2>
              <button className="nav-btn" onClick={nextMonth} data-testid="next-month-btn" aria-label="Next month">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </button>
            </div>

            <div className="calendar-grid">
              {dayNames.map(day => (
                <div key={day} className="day-name">{day}</div>
              ))}
              {renderCalendar()}
            </div>

            <div className="legend">
              <div className="legend-left">
                <div className="legend-item"><span className="legend-color menstruation-color"></span><span className="legend-text">{t("cycleTracker.legend.menstruation")}</span></div>
                <div className="legend-item"><span className="legend-color fertile-color"></span><span className="legend-text">{t("cycleTracker.legend.fertile")}</span></div>
                <div className="legend-item"><span className="legend-color flow-color"></span><span className="legend-text">{t("cycleTracker.legend.flowMoodSymptoms")}</span></div>
              </div>
              <div className="legend-right">
                <div className="legend-item"><span className="legend-color ovulation-color"></span><span className="legend-text">{t("cycleTracker.legend.ovulation")}</span></div>
                <div className="legend-item"><span className="legend-color predicted-color"></span><span className="legend-text">{t("cycleTracker.legend.follicular")}</span></div>
              </div>
            </div>
          </div>

          <button className="insight-btn" onClick={toggleInsight} data-testid="insight-btn">{t("cycleTracker.buttons.insight")}</button>

          {showInsight && (
            <div className="info-card" data-testid="period-info-card">
              <div className="info-header">
                <span className="info-date">{formatSelectedDate()}</span>
                <div className="info-actions">
                  {isSelectedInPeriod ? (
                    <button className="info-badge" onClick={openEndPeriodModal} data-testid="end-period-btn">
                      {t("cycleTracker.buttons.endPeriod")}
                    </button>
                  ) : (
                    <button className="info-badge" onClick={openLogModal} data-testid="log-period-btn">
                      {t("cycleTracker.buttons.logPeriod")}
                    </button>
                  )}
                </div>
              </div>
              <div className="info-content">
                {summaryData && insightRows.length > 0 && (
                  <table className="table">
                    <tbody>
                      {insightRows.map((row, i) => (
                        <tr key={i}>
                          <td>{row.label}</td>
                          <td>{row.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {currentLogData && (
                <div className="logged-data-summary" data-testid="logged-data-summary">
                  <div className="logged-data-header">{t("cycleTracker.tabs.history")}:</div>
                  <div className="logged-data-items">
                    {currentLogData?.flowIntensity && (() => {
                      const item = getImageByName('flow', currentLogData.flowIntensity);
                      return (
                        <div className="logged-item">
                          {item && <img src={item.image} alt={item.name} width={20} />}
                          <span>{currentLogData.flowIntensity}</span>
                        </div>
                      );
                    })()}
                    {currentLogData?.moods?.map((mood, i) => {
                      const item = getImageByName('mood', mood);
                      return (
                        <div key={i} className="logged-item">
                          {item && <img src={item.image} alt={item.name} width={20} />}
                          <span>{mood}</span>
                        </div>
                      );
                    })}
                    {currentLogData?.symptoms?.map((symptom, i) => {
                      const item = getImageByName('symptom', symptom);
                      return (
                        <div key={i} className="logged-item">
                          {item && <img src={item.image} alt={item.name} width={20} />}
                          <span>{symptom}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="recommended-card" data-testid="recommended-card">
            <h3 className="recommended-title">{t("cycleTracker.recommendedTitle")}</h3>
            <div className="recommended-image">
              <svg width="200" height="200" viewBox="0 0 200 200" fill="none">
                <ellipse cx="90" cy="180" rx="80" ry="15" fill="#E8D5F2" opacity="0.4" />
                <path d="M60 80 Q65 60 70 80 Q75 100 80 80 Q85 60 90 80" fill="#A8C8FF" opacity="0.6" />
                <circle cx="100" cy="70" r="25" fill="#FFB8B8" />
                <path d="M85 75 Q90 85 95 75 Q100 65 105 75 Q110 85 115 75" fill="#4A5568" />
                <rect x="60" y="95" width="80" height="90" rx="5" fill="#6B7FD7" stroke="#2D3748" strokeWidth="3" />
                <rect x="70" y="105" width="30" height="25" fill="#E53E3E" />
                <polygon points="85,115 90,110 95,115 90,120" fill="white" />
                <rect x="105" y="105" width="30" height="25" fill="#FC8181" opacity="0.6" />
                <rect x="70" y="135" width="12" height="6" rx="2" fill="#B794F6" opacity="0.7" />
                <rect x="85" y="135" width="12" height="6" rx="2" fill="#B794F6" opacity="0.7" />
                <rect x="100" y="135" width="12" height="6" rx="2" fill="#B794F6" opacity="0.7" />
                <rect x="115" y="135" width="12" height="6" rx="2" fill="#B794F6" opacity="0.7" />
                <path d="M140 160 Q145 155 150 160 L145 175 Q140 180 135 175 Z" fill="#FFB8B8" />
                <rect x="135" y="160" width="15" height="25" rx="7" fill="#6B7FD7" />
                <path d="M50 165 Q55 160 60 165 L55 180 Q50 185 45 180 Z" fill="#FFB8B8" />
                <rect x="45" y="165" width="15" height="25" rx="7" fill="#6B7FD7" />
              </svg>
            </div>
          </div>
          <div className="log-section">
            <p className="log-text">{t("cycleTracker.logText")}</p>
            <button className="log-btn" onClick={openLogFlowMoodSymptoms} data-testid="log-btn">{t("cycleTracker.buttons.log")}</button>
          </div>
        </>
      )}

      {activeTab === 'pregnancy' && (
        <div className="pregnancy-container" data-testid="pregnancy-container">
          {!isPregnancyMode ? (
            <>
              <div className="pregnancy-info-box">
                <h2 className="pregnancy-title">{t('cycleTracker.pregnancy.title')}</h2>
                <p className="pregnancy-desc">{t('cycleTracker.pregnancy.description1')}</p>
                <p className="pregnancy-desc">{t('cycleTracker.pregnancy.description2')}</p>
              </div>
              <div className="pregnancy-action">
                <button className="log-pregnancy-btn" onClick={() => openPregnancyConfirm(1)} data-testid="log-pregnancy-btn">
                  {t('cycleTracker.buttons.logPregnancy')}
                </button>
              </div>
            </>
          ) : (
            <div className="pregnancy-week-card" data-testid="pregnancy-week-card">
              <div className="pregnancy-date-nav" data-testid="pregnancy-date-nav">
                <button
                  className="nav-btn"
                  onClick={() => shiftPregnancyDate(-1)}
                  data-testid="pregnancy-prev-day-btn"
                  aria-label="Previous day"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="15 18 9 12 15 6"></polyline>
                  </svg>
                </button>
                <input
                  type="date"
                  className="pregnancy-date-input"
                  value={pregnancyViewDate}
                  onChange={(e) => e.target.value && goToPregnancyDate(e.target.value)}
                  data-testid="pregnancy-date-input"
                  aria-label={formatPregnancyViewDate()}
                />
                <button
                  className="nav-btn"
                  onClick={() => shiftPregnancyDate(1)}
                  data-testid="pregnancy-next-day-btn"
                  aria-label="Next day"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="9 18 15 12 9 6"></polyline>
                  </svg>
                </button>
              </div>
              {pregnancyWeekData?.img && (
                <img src={pregnancyWeekData.img} alt="" className="pregnancy-week-image" />
              )}
              {pregnancyWeekData?.weeks !== undefined && pregnancyWeekData?.weeks !== '' && (
                <div className="pregnancy-week-badge">
                  {t('cycleTracker.pregnancy.weekLabel')} {pregnancyWeekData.weeks}
                </div>
              )}
              {pregnancyWeekData?.title && <h3 className="pregnancy-week-title">{pregnancyWeekData.title}</h3>}
              {pregnancyWeekData?.subtitle && <p className="pregnancy-week-subtitle">{pregnancyWeekData.subtitle}</p>}
              {pregnancyWeekData?.dev && (
                <div className="pregnancy-week-section" dangerouslySetInnerHTML={{ __html: pregnancyWeekData.dev }} />
              )}
              {pregnancyWeekData?.lifestyle && (
                <div className="pregnancy-week-section" dangerouslySetInnerHTML={{ __html: pregnancyWeekData.lifestyle }} />
              )}
              <button className="pregnancy-deactivate-btn" onClick={() => openPregnancyConfirm(0)} data-testid="deactivate-pregnancy-btn">
                {t('cycleTracker.pregnancy.deactivate')}
              </button>
            </div>
          )}
        </div>
      )}

      {showLogModal && (
        <div className="modal-overlay" onClick={closeLogModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <p className="modal-text">{t("cycleTracker.modal.periodText")}</p>
            <div className="modal-buttons">
              <button className="modal-btn log-period-btn" onClick={logPeriod} disabled={loading}>
                {loading ? t("cycleTracker.modal.logPeriod") + '...' : t("cycleTracker.buttons.logPeriod")}
              </button>
              <button className="modal-btn cancel-btn" onClick={closeLogModal} disabled={loading}>{t("cycleTracker.buttons.cancel")}</button>
            </div>
          </div>
        </div>
      )}

      {showEndPeriodModal && (
        <div className="modal-overlay" onClick={closeEndPeriodModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <p className="modal-text">
              {isDeletingPeriod ? t('cycleTracker.modal.deletePeriodText') : t('cycleTracker.modal.endPeriodText')}
            </p>
            <div className="modal-buttons">
              <button className="modal-btn log-period-btn" onClick={confirmEndPeriod} disabled={loading}>
                {loading ? t("cycleTracker.buttons.endPeriod") + '...' : t("cycleTracker.buttons.endPeriod")}
              </button>
              <button className="modal-btn cancel-btn" onClick={closeEndPeriodModal} disabled={loading}>{t("cycleTracker.buttons.cancel")}</button>
            </div>
          </div>
        </div>
      )}

      {showPregnancyConfirmModal && (
        <div className="modal-overlay" onClick={closePregnancyConfirm}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <p className="modal-text">
              {pendingPregnancyFlag === 1
                ? t('cycleTracker.modal.activatePregnancyText')
                : t('cycleTracker.modal.deactivatePregnancyText')}
            </p>
            <div className="modal-buttons">
              <button className="modal-btn cancel-btn" onClick={closePregnancyConfirm} disabled={loading}>{t("cycleTracker.buttons.cancel")}</button>
              <button className="modal-btn log-period-btn" onClick={() => activatePregnancy(pendingPregnancyFlag)} disabled={loading}>
                {loading ? t("cycleTracker.buttons.activate") + '...' : t("cycleTracker.buttons.activate")}
              </button>
            </div>
          </div>
        </div>
      )}

      {showFaqModal && (
        <div className="modal-overlay" onClick={closeFaqModal}>
          <div className="modal-content faq-modal" onClick={(e) => e.stopPropagation()}>
            <div className="faq-header">
              <h3 className="faq-title">{t('cycleTracker.faq.title')}</h3>
              <button className="modal-close-btn" onClick={closeFaqModal} aria-label={t('cycleTracker.buttons.close')}>&times;</button>
            </div>
            <div className="faq-list">
              {faqItems.map((item, i) => (
                <div key={i} className={`faq-item${openFaqIndex === i ? ' open' : ''}`}>
                  <button className="faq-question" onClick={() => setOpenFaqIndex(openFaqIndex === i ? -1 : i)}>
                    <span>{item.q}</span>
                    <span className="faq-caret">{openFaqIndex === i ? '−' : '+'}</span>
                  </button>
                  {openFaqIndex === i && <div className="faq-answer">{item.a}</div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CycleTracker;
